import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Spin, message } from "antd";
import { AUTH_TOKEN, DOMAIN_ID, ORG_ID, REACT_APP_API_TENANTS_URL } from "../../../constants";
import { SettingsPage, SettingsRow, SettingsSection, SettingsStatus } from "../settingsUi";

const FLUENTBIT_OUTPUT = [
  {
    id: 1,
    title: "Splunk",
    description:
      "Splunk allows you to search, analyze, and visualize machine data in real-time—turning logs, events, and metrics into security insights, operational intelligence, and actionable answers.",
    icon: "https://www.splunk.com/content/dam/splunk2/en_us/images/icon-library/footer/logo-splunk-corp-rgb-k-web.svg",
    link: "https://docs.fluentbit.io/manual/pipeline/outputs/splunk",
    color: "#0070CD",
    subtitle: "Connect to your Splunk instance via HTTP Event Collector",
  },
  {
    id: 2,
    title: "HTTP",
    description:
      "The HTTP output plugin allows you to flush your records to an HTTP endpoint. It supports both HTTP and HTTPS and provides options for authentication, payload format, and retry mechanisms.",
    icon: "https://cdn-icons-png.flaticon.com/512/2165/2165004.png",
    link: "https://docs.fluentbit.io/manual/pipeline/outputs/http",
    color: "#7635DC",
    subtitle: "Send logs to any HTTP/HTTPS endpoint",
  },
  {
    id: 3,
    title: "Syslog",
    description:
      "The Syslog output plugin allows you to deliver records to a Syslog server through TCP or UDP. It follows the RFC5424 protocol standard for message formatting.",
    icon: "https://cdn-icons-png.flaticon.com/512/8888/8888205.png",
    link: "https://docs.fluentbit.io/manual/pipeline/outputs/syslog",
    color: "#00A76F",
    subtitle: "Stream logs to your Syslog server via UDP or TCP",
  },
  {
    id: 4,
    title: "Elasticsearch",
    description:
      "The Elasticsearch output plugin allows you to ingest your records into an Elasticsearch database. It supports template management, HTTP authentication, and TLS/SSL encryption.",
    icon: "https://assets.zabbix.com/img/brands/elasticsearch.svg",
    link: "https://docs.fluentbit.io/manual/pipeline/outputs/elasticsearch",
    color: "#F6AD37",
    subtitle: "Index logs directly into Elasticsearch",
  },
];

// Settings → Logs Configuration: the log outputs this tenant can forward to,
// each a row that opens its form (/pam/settings/logsConfig/output?op=…).
export default function LogsOutputConfig() {
  const navigate = useNavigate();

  const [existingConfigs, setExistingConfigs] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch existing configurations on component mount
  useEffect(() => {
    setLoading(true);

    axios
      .post(
        `${REACT_APP_API_TENANTS_URL}/getLogOutputConfiguration`,
        {
          orgId: ORG_ID(),
          tenantId: DOMAIN_ID(),
        },
        {
          headers: {
            withCredentials: true,
            "X-Authorization": AUTH_TOKEN(),
          },
        }
      )
      .then((response) => {
        setLoading(false);
        if (response.data && response.data.code !== 500) {
          setExistingConfigs(response.data);
        } else {
          // If error 500, initialize with empty configs
          setExistingConfigs({
            splunk: { Host: "", Port: 0 },
            http: { Host: "", Port: 0 },
            syslog: { Host: "", Port: 0 },
            elasticsearch: { Host: "", Port: 0 },
          });
        }
      })
      .catch((error) => {
        setLoading(false);
        console.error("Error fetching log configurations:", error?.response?.status, error?.message);
        message.error("Failed to fetch existing configurations");

        // Initialize with empty configs on error
        setExistingConfigs({
          splunk: { Host: "", Port: 0 },
          http: { Host: "", Port: 0 },
          syslog: { Host: "", Port: 0 },
          elasticsearch: { Host: "", Port: 0 },
        });
      });
  }, []);

  // Function to get correct case mapping for output type
  const getOutputTypeKey = (type) => {
    switch (type) {
      case "Splunk":
        return "splunk";
      case "HTTP":
        return "http";
      case "Syslog":
        return "syslog";
      case "Elasticsearch":
        return "elasticsearch";
      default:
        return type.toLowerCase();
    }
  };

  // Check if configuration exists for a specific output type. The tenant has
  // one active output: getLogOutputConfiguration's outputName ("Splunk", "Http",
  // "Syslog", "ElasticSearch"), and only that one's row is returned.
  const hasConfig = (outputType) => {
    if (!existingConfigs) return false;
    if (typeof existingConfigs.outputName === "string") {
      return existingConfigs.outputName.toLowerCase() === getOutputTypeKey(outputType);
    }

    const config = existingConfigs[getOutputTypeKey(outputType)];
    if (!config) return false;

    // Check if at least host and port are defined
    return config.Host && config.Port;
  };

  return (
    <SettingsPage
      title="Logs Configuration"
      description="Forward authentication and policy events to your logging stack. Choose the output that fits your infrastructure; the tenant sends its logs to one output at a time."
    >
      {loading ? (
        <div style={{ padding: 60, textAlign: "center" }}>
          <Spin tip="Loading configurations...">
            <div style={{ minHeight: 40 }} />
          </Spin>
        </div>
      ) : (
        <SettingsSection title="Outputs" description="Select an output to configure it, or to edit its saved configuration.">
          {FLUENTBIT_OUTPUT.map((output, i) => {
            const configured = !!hasConfig(output.title);
            return (
              <SettingsRow
                key={output.id}
                icon={<img src={output.icon} alt="" style={{ width: 20, height: 20, objectFit: "contain" }} />}
                title={output.title}
                description={output.subtitle}
                value={
                  <SettingsStatus tone={configured ? "success" : "neutral"}>
                    {configured ? "Configured" : "Not configured"}
                  </SettingsStatus>
                }
                last={i === FLUENTBIT_OUTPUT.length - 1}
                onClick={() => navigate(`/pam/settings/logsConfig/output?op=${output.title}`)}
              />
            );
          })}
        </SettingsSection>
      )}
    </SettingsPage>
  );
}
